import OVERLAY_STYLES from "../utils/styles";
import { editablePopupCallbacks } from '../utils/popup-callbacks';
import { deleteEventOverlay, saveEvent, getPointData, getEventDrawableAttachments } from '../api';
import translator from '../utils/translation';
import { Storage, STORAGE_KEYS } from '../utils/store';

/**
 * Extension du geojson layer, permettant d'affichier sur la carte les 
 * éléments d'un événement
 */
const eventDetails = L.GeoJSON.extend({
    initialize: function (event, options) {
        this._event = event;

        const displayId = this._event?.local_id;
        this._groupName = `Evénement ${displayId}`;
        // Référence des couches (features) déjà enregistrées dans le LayerControl pour éviter les doublons
        // key: feature.id, value: layer
        this._registeredFeatureLayers = new Map();
        // Référence des couches d'attachements pour nettoyage
        this._registeredAttachmentLayers = new Set();

        // Appel de la fonction parente
        L.GeoJSON.prototype.initialize.call(this, event, {
            pointToLayer: this._setCustomMarker,
            style: this._setFeatureStyle,
            onEachFeature: this._onEachFeature.bind(this),
            ...options
        });

        this.fetchDrawableAttachment();

        options.eventBus?.on('pm:create', this._addOverlay, this);
        options.eventBus?.on('pm:remove', this._removeOverlay, this);
        options.eventBus?.on('pm:dragend', this._updateOverlay, this);
    },

    onAdd: function (map) {
        L.GeoJSON.prototype.onAdd.call(this, map);

        // Enregistrer les features existantes qui n'ont pas encore été enregistrées dans le LayerControl
        this.eachLayer(layer => {
            this._registerFeatureInLayerControl(layer);
        });
    },

    update: function (event, { renderDetails = false } = {}) {
        this._event = event;
        this._refresh({ renderDetails });
    },

    _addOverlay: function (e) {
        this._map.pm.disableDraw();

        // Préparation de l'overlay pour une création en base par défaut
        const name = translator.t(`event.overlay.type.${this._map._typeToDraw.name}`);
        e.layer.feature = {
            type: 'Feature',
            properties: {
                name,
                type: this._map._typeToDraw
            }
        };  
        
        // Activer Geoman sur le nouveau layer immédiatement (drag only)
        if (e.layer.pm) {
            e.layer.pm.enable({
                allowSelfIntersection: false,
                draggable: true,
                snappable: true,
                allowEditing: false,
                allowRemoval: false,
                allowRotation: false
            });
            e.layer.on('pm:dragend', (evt) => {
                this._updateOverlay(evt);
            });
        }

        this._event.features.push(e.layer.toGeoJSON());

        saveEvent(this._event).then(event => {
            this._map._typeToDraw = null;
            this._event = event;
            this._refresh();

            if (this._map?.hasLayer(e.layer)) {
                this._map.removeLayer(e.layer);
            }

            M.toast({html: 'Elément sauvegardé', classes: 'green accent-4'});
        });
    },

    _removeOverlay: function(event) {
        const feature = event.layer.feature;
        const type = feature?.properties?.origine ?? feature?.properties?.type?.name ?? feature?.properties?.type;
        if (['event', 'internaloverlay', 'reference'].includes(type)) {
            return;
        }

        // Supprimer l'overlay du LayerControl s'il est référencé
        const fid = event?.layer?.feature?.id || event?.layer?.feature?.properties?.id;
        if (fid && this._map?._layerControl && this._registeredFeatureLayers?.has(fid)) {
            try {
                this._map._layerControl.removeLayer(this._registeredFeatureLayers.get(fid));
            } catch (e) { /* noop */ }
            this._registeredFeatureLayers.delete(fid);
        }

        deleteEventOverlay(event.layer.feature.id);
    },

    _updateOverlay: function (e) {
        const feature = e.layer.toGeoJSON();
        const featureId = feature.id || e.layer.feature?.id;
        
        const index = this._event.features.findIndex(f => (f.id || f.properties?.id) === featureId);

        if (index !== -1) {
            this._event.features[index] = feature;
            // On s'assure que l'ID est bien à la racine du GeoJSON pour la sauvegarde
            if (!this._event.features[index].id && featureId) {
                this._event.features[index].id = featureId;
            }

            const type = feature.properties?.origine ?? feature.properties?.type?.name ?? feature.properties?.type;
            if (type === 'event' && feature.geometry.type === 'Point') {
                const coor = {
                    lat: feature.geometry.coordinates[1],
                    lng: feature.geometry.coordinates[0]
                };

                getPointData(coor).then(data => {
                    this._event.massif = data.mountain;
                    this._event.tgi = data.tgi;
                    this._event.com = data.name;
                    this._event.alt = data.alt;

                    this._save(this._event);
                }).catch(() => {
                    this._save(this._event);
                });
            } else {
                this._save(this._event);
            }
        }
    },

    fetchDrawableAttachment: function(id = null) {
        getEventDrawableAttachments(this._event.id, id).then(collections => {
            if (!Array.isArray(collections)) { return; }

            const map = this._map;
            if (!map) {
                this.once('add', () => this.fetchDrawableAttachment(id));
                return;
            }

            collections.forEach(collection => {
                const l = L.geoJSON(collection, {
                    pointToLayer: this._setCustomMarker,
                    style: this._setFeatureStyle,
                    onEachFeature: this._onEachFeature.bind(this),
                });
                l.addTo(map);

                if (map._layerControl) {
                    map._layerControl.addOverlay(l, collection.name, { groupName: this._groupName });
                }
                this._registeredAttachmentLayers.add(l);
            });
        });
    },

    _save: function (event) {
        saveEvent(event).then(updatedEvent => {
            this._event = updatedEvent;
            M.toast({ html: 'Elément mis à jour', classes: 'green accent-4' });
        });
    },

    _refresh: function ({ renderDetails = true } = {}) {
        // Nettoyer le layerControl des overlays précédents pour cet événement
        if (this._map?._layerControl && this._registeredFeatureLayers?.size) {
            this._registeredFeatureLayers.forEach((lyr) => {
                try { this._map._layerControl.removeLayer(lyr); } catch (e) { /* noop */ }
            });
            this._registeredFeatureLayers.clear();
        }

        // Nettoyer les layers d'attachements
        if (this._registeredAttachmentLayers?.size) {
            this._registeredAttachmentLayers.forEach((lyr) => {
                if (this._map?._layerControl) {
                    try { this._map._layerControl.removeLayer(lyr); } catch (e) { /* noop */ }
                }
                if (this._map?.hasLayer(lyr)) {
                    this._map.removeLayer(lyr);
                }
            });
            this._registeredAttachmentLayers.clear();
        }

        this.clearLayers();
        this.addData(this._event.features);

        this.fetchDrawableAttachment();

        this._map.event = this._event;

        if (renderDetails) {
            this._map._renderEventDetails();
        }
    },

    _registerFeatureInLayerControl: function (layer) {
        if (!this._map?._layerControl) {
            return;
        }

        const feature = layer.feature;
        const name = feature.properties?.name ?? `${feature.geometry.type} ${feature.id}`;
        const fid = feature.id || feature.properties?.id;

        if (fid && !this._registeredFeatureLayers.has(fid)) {
            this._map._layerControl.addOverlay(layer, name, { groupName: this._groupName });
            this._registeredFeatureLayers.set(fid, layer);
        }
    },

    _setCustomMarker: function (feat, latlng) {
        return L.gendlocMarker('', latlng, {
            properties: {id: feat.id, ...feat.properties}
        });
    },

    _setFeatureStyle: function (feature) {
        const type = feature.properties?.type?.name ?? feature.properties?.type;
        const style = OVERLAY_STYLES[type];
        const defaultStyle = typeof style === 'function' ? style(feature.properties) : {};
        const overrideStyle = typeof feature.properties.style === 'string' 
            ? JSON.parse(feature.properties.style) 
            : (feature.properties.style || {});

        return {
            ...defaultStyle,
            ...overrideStyle
        };
    },

    _onEachFeature: function (feature, layer) {
        const type = feature.geometry?.type ?? feature.type;
        const name = feature.properties?.name ?? `${type} ${feature.id}`;
        layer.bindTooltip(name, { direction: 'top' });

        const featureType = feature.properties?.origine ?? feature.properties?.type?.name ?? feature.properties?.type;
        const isRemovable = !['event', 'internaloverlay', 'reference'].includes(featureType);
        const isEditable = !['internaloverlay', 'reference'].includes(featureType);
        const isDraggable = !['internaloverlay', 'reference'].includes(featureType);

        // Activer Geoman sur chaque layer individuellement pour être sûr - il y a surement une redondance
        if (layer.pm) {
            layer.pm.enable({
                allowSelfIntersection: false,
                draggable: isDraggable,
                snappable: true,
                allowEditing: isEditable,
                allowRemoval: isRemovable,
                allowRotation: false
            });
        }

        layer.on('pm:dragend', (e) => {
            this._updateOverlay(e);
        });

        this._registerFeatureInLayerControl(layer);

        layer.on('click', (e) => {
            const globalLayer = this;
            const helicos = Storage.get(STORAGE_KEYS.HELICO) ?? [];
            const helicopter = helicos.find(h => h.id === (this._event.helicopter?.id || this._event.helicopter));

            layer.bindPopup(L.overlayPopup(
                feature.properties,
                {
                    editable: true,
                    callbacks: editablePopupCallbacks(globalLayer, layer),
                    latlng: e.latlng,
                    featureId: feature.id,
                    squad: this._event.squad_code,
                    local_id: this._event.local_id,
                    evt_id: this._event.id,
                    com: this._event.com,
                    helicopter: helicopter
                }
            )).openPopup();
        });
    },

});

(() => {
    L.eventDetails = (event, options) => new eventDetails(event, options);
})();